import { type JSX, useMemo, useState } from 'react';

import { PRODUKTARTNAME, type Artikelzeile, type Produktart } from '../api/produkte';
import { Dialog } from '../komponenten/Dialog';

/**
 * Das Produkt einer neuen Buchung wählen (BUC-80, BUC-105; 19.09.2026).
 *
 * DIE LISTE KOMMT VON DER MASKE und wird hier nicht neu geholt: Sie ist schon
 * nach der Kundenart des gewählten Kunden gefiltert (BUC-105). Eine eigene
 * Abfrage an dieser Stelle hätte dieselbe Frage ein zweites Mal zu stellen —
 * und könnte sie anders beantworten.
 *
 * Gefiltert wird im Fenster: Bezeichnung, Artikelnummer, Warengruppe und die
 * Produktart. Es sind Dutzende Einträge, nicht Tausende.
 */
export function Produktsuche({
  artikel,
  beiWahl,
  beiSchliessen,
}: {
  readonly artikel: readonly Artikelzeile[];
  readonly beiWahl: (artikel: Artikelzeile) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [text, setzeText] = useState('');
  const [art, setzeArt] = useState<Produktart | ''>('');

  const arten = useMemo(
    () => [...new Set(artikel.map((a) => a.produktart))].sort(),
    [artikel],
  );

  const treffer = useMemo(() => {
    const w = text.trim().toLocaleLowerCase('de-DE');
    return artikel.filter(
      (a) =>
        (art === '' || a.produktart === art)
        && (w === ''
          || a.bezeichnung.toLocaleLowerCase('de-DE').includes(w)
          || a.artikelnummer.toLocaleLowerCase('de-DE').includes(w)
          || (a.warengruppenname ?? '').toLocaleLowerCase('de-DE').includes(w)),
    );
  }, [artikel, text, art]);

  return (
    <Dialog titel="Produkt auswählen" breite="breit" beiSchliessen={beiSchliessen}>
      <div className="suchmaske__felder">
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="ps-text">
            Bezeichnung, Artikelnummer oder Warengruppe
          </label>
          <input
            id="ps-text"
            className="feld__eingabe"
            value={text}
            onChange={(e) => setzeText(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="ps-art">
            Produktart
          </label>
          <select
            id="ps-art"
            className="feld__eingabe"
            value={art}
            onChange={(e) => setzeArt(e.target.value as Produktart | '')}
          >
            <option value="">jede</option>
            {arten.map((a) => (
              <option key={a} value={a}>
                {PRODUKTARTNAME[a]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {treffer.length === 0 ? (
        <p className="liste__leer">
          {artikel.length === 0
            ? 'Diesem Kunden wird derzeit kein Produkt angeboten.'
            : 'Kein Produkt zu dieser Eingabe.'}
        </p>
      ) : (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Produkte">
            <thead>
              <tr>
                <th scope="col">Produkt</th>
                <th scope="col">Art</th>
                <th scope="col">Warengruppe</th>
              </tr>
            </thead>
            <tbody>
              {treffer.map((a) => (
                <tr key={a.id}>
                  <td>
                    <button type="button" className="knopf knopf--verweis" onClick={() => beiWahl(a)}>
                      {a.artikelnummer} — {a.bezeichnung}
                    </button>
                  </td>
                  <td>{PRODUKTARTNAME[a.produktart] ?? a.produktart}</td>
                  <td>{a.warengruppenname ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
